import React, { useEffect, useRef, useState } from 'react';

/**
 * Compact club brand: the crest, a headline that slides between the club name
 * and "Club Portal 4.0", the district underneath, and a tiny voice-wave that
 * moves only while the headline changes -- as if the portal were announcing it.
 *
 * Used at the top of the sidebar and on the sign-in screen. It draws nothing
 * but flat shapes (no glow) and takes its text colour from the parent.
 * With reduced motion it simply shows the first line.
 */

const LINES = ['Leo Club of SLIIT', 'Club Portal 4.0'];
const SUB = 'District 306 D6';
const INTERVAL_MS = 3600;
const SPEAK_MS = 1300;

export default function BrandTicker({ href, size = 30, lines = LINES, sub = SUB, className = '' }) {
  const [idx, setIdx] = useState(0);
  const [prev, setPrev] = useState(-1);
  const [speaking, setSpeaking] = useState(false);
  const speakTimer = useRef(null);

  useEffect(() => {
    if (lines.length < 2) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const id = setInterval(() => {
      if (document.hidden) return; // don't queue slides in a background tab
      setIdx((cur) => {
        setPrev(cur);
        return (cur + 1) % lines.length;
      });
      setSpeaking(true);
      clearTimeout(speakTimer.current);
      speakTimer.current = setTimeout(() => setSpeaking(false), SPEAK_MS);
    }, INTERVAL_MS);

    return () => {
      clearInterval(id);
      clearTimeout(speakTimer.current);
    };
  }, [lines.length]);

  // The widest line reserves the width so the row never jumps as text swaps.
  const widest = lines.reduce((a, b) => (b.length > a.length ? b : a), '');

  const Tag = href ? 'a' : 'div';
  const linkProps = href ? { href } : {};

  return (
    <Tag className={`bt ${className}`} {...linkProps} aria-label={`${lines.join(', ')}, ${sub}`}>
      <img className="bt-crest" src="/club.png" alt="" width={size} height={size}
           style={{ width: size, height: size }} draggable="false"
           onError={(e) => { e.currentTarget.src = '/default-logo.png'; }} />
      <span className="bt-text" aria-hidden="true">
        <span className="bt-slot">
          <span className="bt-size">{widest}</span>
          {lines.map((line, i) => (
            <span key={line} className={`bt-line ${i === idx ? 'is-on' : i === prev ? 'is-out' : ''}`}>{line}</span>
          ))}
        </span>
        <span className="bt-sub">{sub}</span>
      </span>
      <span className={`bt-wave ${speaking ? 'is-speaking' : ''}`} aria-hidden="true">
        <i /><i /><i /><i />
      </span>
    </Tag>
  );
}
